<template>
	<div
		class="panel"
		:class="{'is-dragging':isDragging}"
		:draggable="draggable"
		@dragstart="dragStart"
		@dragend="dragEnd"
		@dragenter="dragEnter"
		@dragleave="dragLeave"
		@dragover="dragOver"
		@drop="dragDrop"
	>
		<div
			class="drop-target"
			:class="{'drop-target-border':dragOffset>0}"
			:style="{'height': dragOffset+'px'}"
		></div>
		<slot></slot>
	</div>
</template>

<script setup lang="ts">
import { useDashboardsStore } from '@/stores/Dashboards'
import type { PbxAppDashboardItemCommon } from '@/TcxApi/PbxApp'
import { ref } from 'vue'

const dashboards = useDashboardsStore()

interface Props {
	draggable: boolean
	data: PbxAppDashboardItemCommon
}

const props = defineProps<Props>()
const isDragging = ref(false)
const isOver = ref(false)
const leaveTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined)
const dragOffset = ref(0)

function dragStart(event: DragEvent) {
	if (props.draggable) {
		dashboards.dragItem = props.data
		dashboards.dragItemHeight = (event.target as HTMLElement).clientHeight
		isDragging.value = true
	}
}

function dragEnd() {
	if (props.draggable || isDragging.value) {
		isDragging.value = false
		dashboards.dragItem = undefined
	}
}

function dragEnter(_event: DragEvent) {
	if (!dashboards.dragItem) { return }
	dashboards.dragTarget = props.data
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	if (dashboards.dragItem !== props.data) {
		isOver.value = true
		if (props.data.column === dashboards.dragItem.column && dashboards.dragItem.position === props.data.position - 1) {
			return
		}
		dragOffset.value = dashboards.dragItemHeight ?? 0
	}
}

function dragOver(event: DragEvent) {
	if (!dashboards.dragItem) { return }
	event.preventDefault()
	dashboards.dragTarget = props.data
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	if (dashboards.dragItem !== props.data) {
		isOver.value = true
		if (props.data.column === dashboards.dragItem.column && dashboards.dragItem.position === props.data.position - 1) {
			return
		}
		dragOffset.value = dashboards.dragItemHeight ?? 0
	}
}

function dragLeave(_event: DragEvent) {
	if (!dashboards.dragItem) { return }
	dashboards.dragTarget = undefined
	if (dashboards.dragItem !== props.data) {
		leaveTimer.value = setTimeout(() => {
			dragOffset.value = 0
			isOver.value = false
			leaveTimer.value = undefined
		}, 100)
	}
}

function dragDrop(event: DragEvent) {
	if (!dashboards.dragItem) { return }
	event.preventDefault()
	if (dashboards.dragItem !== props.data) {
		dragOffset.value = 0
	}
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	dashboards.dragDropped()
}
</script>

<style scoped>
	.is-dragging {
		opacity: 0.2;
	}
	.panel {
		background: var(--bg-panel);
		border-radius: 5px;
		transition: padding-top 500ms;
	}
	.drop-target {
		transition: 500ms;
		border-radius: 5px 5px;
	}
	.drop-target-border {
		border: 1px dashed var(--border-color-dragdrop);
		background-color: var(--bg-color-dragdrop);
		margin-bottom: 10px;
	}
</style>
